import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { TechPartner } from '@/components/tech-partner';

export function Hero() {
  const { t } = useTranslation();
  return (
    <div id="hero" className="relative min-h-[80vh] md:h-screen w-full overflow-hidden">
      {/* Background GIF with Overlay */}
      <div className="absolute inset-0 z-0">
        {/* Blue gradient overlays for cinematic darkening */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#001a33]/80 via-[#001a33]/60 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.35)_0%,_rgba(0,0,0,0.65)_100%)]" />
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="h-full w-full object-cover"
        >
          <source src="/oleyes_banner_video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center py-16 md:py-0">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto sm:mx-0 text-white px-6 sm:pl-6 sm:pr-0 md:pl-6 lg:pl-6 xl:pl-6 text-center sm:text-left">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide">
              <span className="block pt-3 sm:pt-4 md:pt-5 text-primary-200/90 tracking-wider">
                {t('hero.subtitle')}
              </span>
            </h1>
            <h2 className="mt-3 sm:mt-5 text-lg sm:text-2xl md:text-3xl font-korolev font-medium text-gray-100 max-w-2xl">
              {t('hero.tagline')}
            </h2>
            <p className="mt-2 sm:mt-4 text-xs sm:text-base md:text-lg text-gray-200 max-w-xl leading-relaxed font-poppins">
              {t('hero.description')}
            </p>

            <div className="mt-6 sm:mt-12 flex flex-row gap-3 sm:gap-4 font-poppins">
              <Link href="/free-trial" className="group relative overflow-hidden flex-1 sm:flex-none">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Button
                  size="lg"
                  className="w-full text-black hover:bg-gray-50 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-primary/20"
                >
                  <span className="relative z-10">
                    {t('hero.getTrial')}
                    <ArrowRight className="ml-2 h-2 w-3 inline-block transition-transform group-hover:translate-x-1" />
                  </span>
                </Button>
              </Link>
              <Link
                href="#models"
                className="group relative overflow-hidden flex-1 sm:flex-none"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('models')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full bg-transparent border-white/70 text-white hover:bg-white/10 hover:border-white/90 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-white/10"
                >
                  <span className="relative z-10">{t('hero.learnMore')}</span>
                </Button>
              </Link>
            </div>
            {/* Tech Partners (desktop/tablet): shown under the buttons */}
            <div className="hidden sm:block mt-8">
              <h3 className="text-white/90 font-korolev text-base sm:text-xl font-semibold tracking-wide mb-4">
                {t('partners.technologiesBanner')}
              </h3>
              <div className="bg-transparent/0">
                <TechPartner scroll forceWhite compact />
              </div>
            </div>

          </div>
        </div>
      </div>
      {/* Tech Partners (static) */}
      <div
        className="sm:hidden text-left absolute inset-x-0 bottom-10 px-4"
      >
        <h3 className="text-white/90 font-korolev text-sm font-semibold tracking-wide mb-3 text-center">
          {t('partners.technologiesBanner')}
        </h3>
        <div className="bg-transparent/0">
          <TechPartner scroll forceWhite compact />
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="hidden sm:block absolute bottom-12 md:bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
        <button
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            });
          }}
          aria-label="Scroll down"
          className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
        >
          <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
            {t('hero.scrollDown', 'Scroll down')}
          </span>
          <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
            <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
          </div>
        </button>
      </div>
    </div>
  );
}